import { MouseEvent, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  OutlinedInput,
  Popover,
  Stack,
  ToggleButtonGroup,
  Tooltip,
  Typography as T,
} from "@mui/material";
import ColorCircle from "~/pages/datasets/components/ColorCircle";
import ColorRadio from "~/pages/datasets/components/ColorRadio";
import AccentColor from "~/pages/datasets/types/enums/accent-color";

const COLORS: AccentColor[] = [
  AccentColor.BLUE,
  AccentColor.PINK,
  AccentColor.GREEN,
  AccentColor.ORANGE,
  AccentColor.PURPLE,
];

const DomainDetails = () => {
  const { watch, formState } = useFormContext();
  const [iconAnchorEl, setIconAnchorEl] = useState<null | HTMLElement>(null);
  const iconMenuOpen = Boolean(iconAnchorEl);
  const selectedIcon = watch("icon");

  const handleIconMenuOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setIconAnchorEl(event.currentTarget);
  };

  const handleIconMenuClose = () => {
    setIconAnchorEl(null);
  };

  return (
    <Stack gap={2}>
      <Stack>
        <T variant="h6" fontWeight={550} fontSize={18}>
          Details
        </T>
      </Stack>
      <Box display="flex" gap={1}>
        <Stack>
          <T variant="label" lineHeight={"23px"}>
            Color
          </T>
          <Tooltip title="Update this domain's color" arrow enterDelay={750}>
            <span>
              <Button
                data-testid="color-selector"
                disabled={formState.isSubmitting}
                onClick={handleIconMenuOpen}
                size="small"
                sx={{ borderColor: "grey.400", height: 36, width: 36, p: 0 }}
                variant="outlined"
              >
                <ColorCircle sx={{ backgroundColor: selectedIcon, width: 12, height: 12 }} />
              </Button>
            </span>
          </Tooltip>
          <Popover
            open={iconMenuOpen}
            anchorEl={iconAnchorEl}
            onClose={handleIconMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: 2,
              },
            }}
          >
            <FormControl>
              <Controller
                name="icon"
                render={({ field }) => (
                  <ToggleButtonGroup
                    {...field}
                    id={field.name}
                    size="small"
                    exclusive
                    sx={{
                      p: 0,
                      background: "white",
                    }}
                    value={field.value}
                    onChange={(_, value) => {
                      field.onChange(value);
                      handleIconMenuClose();
                    }}
                  >
                    {COLORS.map((option) => (
                      <ColorRadio
                        key={option}
                        disabled={formState.isSubmitting}
                        selected={selectedIcon === option}
                        showCheck={selectedIcon === option}
                        value={option}
                        onClick={(color) => field.onChange(color)}
                      />
                    ))}
                  </ToggleButtonGroup>
                )}
              />
            </FormControl>
          </Popover>
        </Stack>
        <FormControl>
          <Controller
            name="name"
            rules={{ required: true }}
            render={({ field }) => (
              <Stack>
                <FormLabel htmlFor={field.name}>
                  <T variant="label">Display Name *</T>
                </FormLabel>
                <OutlinedInput
                  {...field}
                  autoComplete="off"
                  disabled={formState.isSubmitting}
                  inputProps={{ "data-1p-ignore": true }}
                  id={field.name}
                  placeholder="Add display name"
                  size="small"
                  sx={{ height: 36 }}
                />
              </Stack>
            )}
          />
        </FormControl>
      </Box>
      <FormControl>
        <Controller
          name="description"
          render={({ field }) => (
            <Stack>
              <FormLabel htmlFor={field.name}>
                <T variant="label">Description</T>
              </FormLabel>
              <OutlinedInput
                {...field}
                autoComplete="off"
                disabled={formState.isSubmitting}
                id={field.name}
                placeholder="Add description"
                size="small"
                sx={{ height: 36, maxWidth: 630 }}
              />
            </Stack>
          )}
        />
      </FormControl>
    </Stack>
  );
};

export default DomainDetails;
